/**
 * Technology Export Configuration
 *
 * ANALYSIS FINDINGS:
 * - Original component: ExportTechnologies.tsx (125 lines)
 * - Filters: NONE (simplest configuration)
 * - Columns: 9 exportable fields (assets_impacted, vendor, product, version, etc)
 * - Storage key: ADVANCE_EXPORT_OPTIONS.technologies (v1 - first migration)
 * - Query: Simple Technology node with no filters or relationships
 *
 * MIGRATION NOTES:
 * - Technologies have NO filter sections (simplest migration)
 * - Just column selection and format selection
 * - Demonstrates universal system works for minimal configurations
 * - Good reference for simple entity exports
 */

import type {
  ExportColumnConfig,
  UniversalExportConfig,
} from '@/components/export/universal/types';
import { GraphQueryBuilder } from '@/components/export/universal/utils';
import type { Technology } from '@/types';

/**
 * Technology column configuration
 * Maps to original ColumnOptions in ExportTechnologies.tsx
 *
 * NOTE: Column order and labels match original component exactly
 */
export const TECHNOLOGY_COLUMNS: readonly ExportColumnConfig[] = [
  {
    id: 'assets_impacted',
    label: 'Assets Impacted',
    defaultSelected: true,
  },
  { id: 'vendor', label: 'Vendor', defaultSelected: true },
  { id: 'product', label: 'Product', defaultSelected: true },
  { id: 'version', label: 'Version', defaultSelected: true },
  { id: 'history', label: 'History', defaultSelected: true },
  { id: 'cpe', label: 'CPE', defaultSelected: true },
  { id: 'comment', label: 'Comment', defaultSelected: true },
  { id: 'created', label: 'First Seen', defaultSelected: true },
  { id: 'visited', label: 'Last Seen', defaultSelected: true },
] as const;

/**
 * Universal export configuration for Technologies
 *
 * FEATURES:
 * - NO filters (simplest configuration)
 * - 9 exportable columns
 * - CSV and JSON formats
 * - Default columns: vendor, product, version
 *
 * SIMPLICITY:
 * - No filter sections
 * - Simple query (just Technology label)
 * - Minimal validation (just columns required)
 */
export const technologyExportConfig: UniversalExportConfig<Technology> = {
  entityType: 'Technology',
  apiEntityType: 'technology',
  storageKey: 'ADVANCE_EXPORT_OPTIONS.technologies',

  // No filter sections for technologies
  filterSections: [],

  columns: TECHNOLOGY_COLUMNS,

  formats: [
    { label: 'CSV', value: 'csv' },
    { label: 'JSON', value: 'json' },
  ],
  defaultFormat: { label: 'CSV', value: 'csv' },

  /**
   * Build graph query for technology export
   *
   * QUERY STRUCTURE:
   * - Labels: ['Technology']
   * - Filters: NONE (simplest query)
   * - Relationships: NONE
   * - OrderBy: name (ascending)
   *
   * BEHAVIOR:
   * - When selectedRows is empty: Export all technologies
   * - When selectedRows has values: Only export selected technologies
   * - No filters to apply (no filter sections)
   */
  buildQuery: (_filters, _selectedRows) => {
    // Simplest possible query - just Technology nodes
    return GraphQueryBuilder.create(['Technology'])
      .orderBy('name', false)
      .build();
  },

  /**
   * Validate export selections
   *
   * VALIDATION RULES:
   * - At least one column must be selected
   * - No filter validation needed (no filters)
   */
  validateExport: (_filters, columns, _selectedRows) => {
    if (columns.length === 0) {
      return [
        {
          field: 'columns' as const,
          message: 'Please select at least one column to export.',
        },
      ];
    }
    return null;
  },
};
